import React, { useContext } from 'react'
import { Link as Linkreact } from 'react-router-dom'
import { Typography, Box, Grid, Card, CardContent, CardActionArea, Stack } from '@mui/material'
import EventNoteSharpIcon from '@mui/icons-material/EventNoteSharp'
import AccountCircleSharpIcon from '@mui/icons-material/AccountCircleSharp'
import DashboardIcon from '@mui/icons-material/Dashboard'

import AuthContext from '../../contexts/AuthContext'

const HomePage = () => {
  const { user } = useContext(AuthContext)

  const navigationCards = [
    {
      title: 'Events',
      description: 'Manage and track all running events',
      icon: EventNoteSharpIcon,
      path: '/events',
      color: 'primary',
      visible: user.role !== 'Staff',
    },
    {
      title: 'Users',
      description: 'Manage user accounts and permissions',
      icon: AccountCircleSharpIcon,
      path: '/users',
      color: 'secondary',
      visible: user.role !== 'Staff',
    },
  ]

  const visibleCards = navigationCards.filter((card) => card.visible)

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        p: { xs: 2, sm: 3, md: 4 },
      }}>
      {/* Header Section */}
      <Box sx={{ mb: { xs: 3, sm: 4, md: 5 } }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 700,
            mb: { xs: 1, sm: 1.25, md: 1.5 },
            mt: { xs: 1, sm: 1.25, md: 1.5 },
            color: 'text.primary',
            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
            lineHeight: 1.3,
          }}>
          Dashboard
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: 'text.secondary',
            fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' },
            mb: { xs: 2, sm: 2.5, md: 3 },
            pb: { xs: 2, sm: 2.5, md: 3 },
            borderBottom: '1px solid',
            borderColor: 'divider',
            lineHeight: 1.6,
          }}>
          Welcome to Racepack Admin Dashboard. Manage your events, participants, and users from
          here.
        </Typography>
      </Box>

      {/* Navigation Cards Section */}
      {visibleCards.length > 0 && (
        <Box sx={{ mb: { xs: 2.5, sm: 3, md: 3.5 } }}>
          <Typography
            variant="h6"
            component="h2"
            sx={{
              fontWeight: 600,
              mb: { xs: 2, sm: 2.5, md: 3 },
              color: 'text.primary',
              fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
              lineHeight: 1.3,
            }}>
            Quick Access
          </Typography>

          <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
            {visibleCards.map((card) => {
              const IconComponent = card.icon
              return (
                <Grid item xs={12} sm={6} md={4} key={card.path}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: { xs: 1.5, sm: 2, md: 2.5 },
                      boxShadow: (theme) =>
                        theme.palette.mode === 'light'
                          ? '0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)'
                          : '0 2px 8px rgba(0, 0, 0, 0.3), 0 1px 3px rgba(0, 0, 0, 0.2)',
                      backgroundColor: (theme) =>
                        theme.palette.mode === 'light' ? '#ffffff' : 'rgba(255, 255, 255, 0.05)',
                      border: (theme) =>
                        `1px solid ${
                          theme.palette.mode === 'light'
                            ? 'rgba(0, 0, 0, 0.08)'
                            : 'rgba(255, 255, 255, 0.08)'
                        }`,
                      transition: (theme) =>
                        theme.transitions.create(['transform', 'box-shadow'], {
                          duration: theme.transitions.duration.short,
                        }),
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: (theme) =>
                          theme.palette.mode === 'light'
                            ? '0 8px 24px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.08)'
                            : '0 8px 24px rgba(0, 0, 0, 0.4), 0 4px 8px rgba(0, 0, 0, 0.3)',
                      },
                    }}>
                    <CardActionArea
                      component={Linkreact}
                      to={card.path}
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        p: { xs: 2, sm: 2.5, md: 3 },
                        textDecoration: 'none',
                        '&:hover': {
                          backgroundColor: 'transparent',
                        },
                      }}>
                      <CardContent
                        sx={{
                          width: '100%',
                          p: 0,
                          '&:last-child': {
                            pb: 0,
                          },
                        }}>
                        <Stack
                          direction="row"
                          spacing={{ xs: 1.5, sm: 2, md: 2.5 }}
                          alignItems="flex-start"
                          sx={{ mb: { xs: 1.5, sm: 2, md: 2.5 } }}>
                          <Box
                            sx={{
                              p: { xs: 1, sm: 1.25, md: 1.5 },
                              borderRadius: { xs: 1.5, sm: 2, md: 2.5 },
                              backgroundColor: (theme) =>
                                card.color === 'primary'
                                  ? theme.palette.primary.main
                                  : theme.palette.secondary.main,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}>
                            <IconComponent
                              sx={{
                                color: '#ffffff',
                                fontSize: {
                                  xs: '1.5rem',
                                  sm: '1.75rem',
                                  md: '2rem',
                                },
                              }}
                            />
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="h6"
                              component="h3"
                              sx={{
                                fontWeight: 600,
                                mb: { xs: 0.5, sm: 0.75, md: 1 },
                                color: 'text.primary',
                                fontSize: {
                                  xs: '1rem',
                                  sm: '1.125rem',
                                  md: '1.25rem',
                                },
                                lineHeight: 1.3,
                              }}>
                              {card.title}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'text.secondary',
                                fontSize: {
                                  xs: '0.8125rem',
                                  sm: '0.875rem',
                                  md: '0.9375rem',
                                },
                                lineHeight: 1.5,
                              }}>
                              {card.description}
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              )
            })}
          </Grid>
        </Box>
      )}

      {/* Welcome Message for Staff */}
      {user.role === 'Staff' && (
        <Box
          sx={{
            mt: { xs: 3, sm: 4, md: 5 },
            p: { xs: 2, sm: 2.5, md: 3 },
            borderRadius: { xs: 1.5, sm: 2, md: 2.5 },
            backgroundColor: (theme) =>
              theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.02)' : 'rgba(255, 255, 255, 0.05)',
            border: (theme) =>
              `1px solid ${
                theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)'
              }`,
            textAlign: 'center',
          }}>
          <DashboardIcon
            sx={{
              fontSize: { xs: '3rem', sm: '3.5rem', md: '4rem' },
              color: 'text.secondary',
              mb: { xs: 1.5, sm: 2, md: 2.5 },
              opacity: 0.5,
            }}
          />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              mb: { xs: 1, sm: 1.25, md: 1.5 },
              color: 'text.primary',
              fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
            }}>
            Welcome to Racepack Admin
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
              lineHeight: 1.6,
            }}>
            You are logged in as Staff. Please contact an administrator for access to management
            features.
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export default HomePage
